gsap.registerPlugin(ScrollTrigger)

// class SectionId {
//   static #separator = '__';

//   // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
//   static parseId(qualifiedSectionId) {
//     return qualifiedSectionId.split(SectionId.#separator)[0];
//   }

//   // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
//   static parseSectionName(qualifiedSectionId) {
//     return qualifiedSectionId.split(SectionId.#separator)[1];
//   }

//   // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
//   static getIdForSection(sectionId, sectionName) {
//     return `${sectionId}${SectionId.#separator}${sectionName}`;
//   }
// }

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    // this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const buttonMinus = this.querySelector("[name='minus']");
      if (parseInt(value) <= parseInt(this.input.min)) {
        buttonMinus.setAttribute('disabled', 'true');
      } else {
        buttonMinus.removeAttribute('disabled');
      }
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("[name='plus']");
      if (value >= max) {
        buttonPlus.setAttribute('disabled', 'true');
      } else {
        buttonPlus.removeAttribute('disabled');
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

// /*
//  * Shopify Common JS
//  *
//  */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

// class ProductRecommendations extends HTMLElement {
//   observer = undefined;

//   constructor() {
//     super();
//   }

//   connectedCallback() {
//     this.initializeRecommendations(this.dataset.productId);
//   }

//   initializeRecommendations(productId) {
//     this.observer?.unobserve(this);
//     this.observer = new IntersectionObserver(
//       (entries, observer) => {
//         if (!entries[0].isIntersecting) return;
//         observer.unobserve(this);
//         this.loadRecommendations(productId);
//       },
//       { rootMargin: '0px 0px 400px 0px' }
//     );
//     this.observer.observe(this);
//   }

//   loadRecommendations(productId) {
//     fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
//       .then((response) => response.text())
//       .then((text) => {
//         const html = document.createElement('div');
//         html.innerHTML = text;
//         const recommendations = html.querySelector('product-recommendations');

//         if (recommendations?.innerHTML.trim().length) {
//           this.innerHTML = recommendations.innerHTML;
//         }

//         if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
//           this.remove();
//         }

//         if (html.querySelector('.grid__item')) {
//           this.classList.add('product-recommendations--loaded');
//         }
//       })
//       .catch((e) => {
//         console.error(e);
//       });
//   }
// }

// customElements.define('product-recommendations', ProductRecommendations);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.searchModal = this.querySelector('[data-search-modal]');
    this.backgroundOverlay = this.querySelector('[data-modal-overlay]');
    this.closeButton = this.querySelector('button[type="button"]');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    this.onKeyDownEvent = this.onKeyDown.bind(this);
    document.addEventListener('keydown', this.onKeyDownEvent);

    this.animation = gsap
      .timeline({ paused: true, defaults: { duration: 0.4, ease: 'expo.inOut' } })
      .fromTo(
        this.searchModal,
        {
          opacity: 0,
          x: '5rem',
        },
        {
          opacity: 1,
          x: 0,
        }
      )
      .fromTo(
        this.backgroundOverlay,
        {
          opacity: 0,
        },
        {
          opacity: 0.2,
        },
        '<'
      )
      .reverse();
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.hasAttribute('data-modal-overlay')) {
      this.close(false);
    }
  }

  onKeyDown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (!this.isOpen()) {
        this.open();
      }
    }
  }

  open(event) {
    this.detailsContainer.setAttribute('open', true);
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    this.animation.play();

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    this.animation.reverse().then(() => {
      this.detailsContainer.removeAttribute('open');
      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove('overflow-hidden');
    });

    removeTrapFocus(focusToggle ? this.summaryToggle : null);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDownEvent);
  }
}

customElements.define('details-modal', DetailsModal);

class LoadingBar extends HTMLElement {
  constructor() {
    super();
    this.startLoading = this.startLoading.bind(this);
    this.endLoading = this.endLoading.bind(this);
  }

  connectedCallback() {
    document.addEventListener('loading:start', this.startLoading);
    document.addEventListener('loading:end', this.endLoading);
  }

  disconnectedCallback() {
    document.removeEventListener('loading:start', this.startLoading);
    document.removeEventListener('loading:end', this.endLoading);
  }

  startLoading() {
    gsap.to(this, { width: '80%', duration: 0.25, ease: 'power2.out' });
  }

  endLoading() {
    gsap.to(this, {
      width: '100%',
      duration: 0.2,
      ease: 'power2.out',
      onComplete: () => {
        gsap.set(this, { width: '0%' });
      },
    });
  }
}

customElements.define('loading-bar', LoadingBar);


const ANNOUNCEMENT_BAR_BREAKPOINT = 1024

class AnnouncementBar extends HTMLElement {
  constructor() {
    super()
    this._intervalId = null
    this._index = 0
    this._boundOnResize = this._onResize.bind(this)
  }

  _isMobile() {
    return window.innerWidth < ANNOUNCEMENT_BAR_BREAKPOINT
  }

  _startRotation() {
    const items = [...this.querySelectorAll('[data-block-index]')]
    if (items.length <= 1) return

    this._index = 0
    gsap.set(items, { autoAlpha: 0, y: 8, display: 'none' })
    gsap.set(items[0], { autoAlpha: 1, y: 0, display: 'flex' })

    this._intervalId = setInterval(() => {
      const current = items[this._index]
      const nextIndex = (this._index + 1) % items.length
      const next = items[nextIndex]

      const tl = gsap.timeline()

      tl.to(current, {
        autoAlpha: 0,
        y: -8,
        duration: 0.1,
        ease: 'power1.out',
        onComplete: () => {
          current.style.display = 'none'
          gsap.set(current, { y: 8 })
        }
      })
        .add(() => {
          next.style.display = 'flex'
        })
        .to(next, {
          autoAlpha: 1,
          y: 0,
          duration: 0.3,
          ease: 'power1.out'
        })

      this._index = nextIndex
    }, 3000)
  }

  _stopRotation() {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId)
      this._intervalId = null
    }
    const items = [...this.querySelectorAll('[data-block-index]')]
    if (items.length === 0) return
    gsap.set(items, { clearProps: 'all' })
    items.forEach((el) => {
      el.style.display = 'flex'
    })
  }

  _onResize() {
    if (this._isMobile()) {
      if (this._intervalId === null) this._startRotation()
    } else {
      if (this._intervalId !== null) this._stopRotation()
    }
  }

  connectedCallback() {
    if (this._isMobile()) this._startRotation()
    window.addEventListener('resize', this._boundOnResize)
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._boundOnResize)
    if (this._intervalId !== null) {
      clearInterval(this._intervalId)
      this._intervalId = null
    }
  }
}

customElements.define('announcement-bar', AnnouncementBar)

class UITooltip extends HTMLElement {
  static active = null

  constructor() {
    super()
    this.isOpen = false
    this.mobileQuery = window.matchMedia('(max-width: 767px)')
    this.isBound = false

    this.onTriggerClick = e => {
      e.stopPropagation()
      if (!this.mobileQuery.matches) return
      // Mobile: open a moment later so the tap state on the trigger is visible first
      clearTimeout(this.openTimer)
      if (this.isOpen) this.close()
      else this.openTimer = setTimeout(() => this.open(), 150)
    }

    this.onTriggerMouseEnter = () => {
      if (!this.mobileQuery.matches) this.open()
    }

    this.onHostMouseLeave = () => {
      if (!this.mobileQuery.matches) this.close()
    }

    this.onDocumentClick = e => {
      if (!this.isConnected) return
      if (!this.contains(e.target)) this.close()
    }

    this.onDocumentKeyDown = e => {
      if (e.key === 'Escape') this.close()
    }

    this.onBackdropClick = () => this.close()
  }

  connectedCallback() {
    this.trigger = this.querySelector('[slot="trigger"]')
    this.content = this.querySelector('[slot="content"]')
    this.backdrop = this.querySelector('[data-backdrop]')

    if (!this.trigger || !this.content) return

    this.content.classList.add(
      'transition-all',
      'duration-120',
      'ease-snappy',
      'opacity-0',
      'pointer-events-none'
    )

    this.bind()
  }

  disconnectedCallback() {
    this.close()
    this.unbind()
    if (UITooltip.active === this) {
      UITooltip.active = null
    }
  }

  bind() {
    if (this.isBound) return

    this.trigger.addEventListener('click', this.onTriggerClick)
    this.trigger.addEventListener('mouseenter', this.onTriggerMouseEnter)
    this.addEventListener('mouseleave', this.onHostMouseLeave)
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onDocumentKeyDown)
    if (this.backdrop) this.backdrop.addEventListener('click', this.onBackdropClick)

    this.isBound = true
  }

  unbind() {
    if (!this.isBound) return

    this.trigger?.removeEventListener('click', this.onTriggerClick)
    this.trigger?.removeEventListener('mouseenter', this.onTriggerMouseEnter)
    this.removeEventListener('mouseleave', this.onHostMouseLeave)
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeyDown)
    this.backdrop?.removeEventListener('click', this.onBackdropClick)

    this.isBound = false
  }

  open() {
    if (this.isOpen) return

    if (UITooltip.active && UITooltip.active !== this) {
      UITooltip.active.close()
    }

    UITooltip.active = this
    this.isOpen = true

    this.trigger.setAttribute('aria-expanded', 'true')

    this.content.hidden = false
    // Let the browser render it once while still transparent, so the fade-in actually runs
    void this.content.offsetHeight
    this.content.classList.remove('opacity-0', 'pointer-events-none')
    this.content.classList.add('opacity-100', 'pointer-events-auto', 'translate-y-0')

    if (this.mobileQuery.matches && this.backdrop) {
      this.backdrop.classList.remove('opacity-0', 'pointer-events-none')
      this.backdrop.classList.add('opacity-100')
    }
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.trigger.setAttribute('aria-expanded', 'false')

    this.content.classList.add('opacity-0', 'pointer-events-none')
    this.content.classList.remove('opacity-100', 'translate-y-0')

    if (UITooltip.active === this) {
      UITooltip.active = null
    }

    if (this.mobileQuery.matches && this.backdrop) {
      this.backdrop.classList.remove('opacity-100')
      this.backdrop.classList.add('opacity-0', 'pointer-events-none')
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }
}

customElements.define('ui-tooltip', UITooltip)

CustomEase.create('smooth', '0.77,0,0.175,1')

class MarqueeSlider extends HTMLElement {
  connectedCallback() {
    const wrappers = this.querySelectorAll('[data-marquee-wrapper]');
    if (!wrappers.length) return;

    const pxPerSecond = parseFloat(this.dataset.pixelsPerSecond || '50') || 50;

    const build = () => {
      if (this.tl) return true;
      const w = wrappers[0].offsetWidth;
      if (!w) return false;

      if (this.dataset.pauseOnHover) {
        wrappers.forEach((wrapper) => {
          wrapper.addEventListener('mouseenter', () => {
            this.tl.pause()
          })
          wrapper.addEventListener('mouseleave', () => {
            this.tl.resume()
          })
        })
      }

      this.tl = gsap.timeline({ repeat: -1 });

      this.tl.to(wrappers, {
        xPercent: -100,
        duration: w / pxPerSecond,
        ease: 'none',
      });
      return true;
    };

    if (!build()) {
      if (document.readyState === 'complete') {
        requestAnimationFrame(() => build());
      } else {
        window.addEventListener('load', () => build(), { once: true });
      }
    }
  }
}

customElements.define('marquee-slider', MarqueeSlider);

window.addEventListener('load', () => {
  ScrollTrigger.refresh()
})

function initAutoHideHeader() {
  const headerSection =
    document.querySelector('.shopify-section-header') ||
    document.querySelector('[id*="shopify-section-header"]');
  if (!headerSection) return;

  const headerEl = headerSection.querySelector('header');

  const thresholdUpAttr =
    (headerEl && headerEl.dataset.thresholdSpeedUp) || headerSection.dataset.thresholdSpeedUp;

  const thresholdDownAttr =
    (headerEl && headerEl.dataset.thresholdSpeedDown) || headerSection.dataset.thresholdSpeedDown;

  let lastScrollY = window.scrollY;
  let lastTime = performance.now();
  let isHidden = false;

  const parsedThresholdUp = parseFloat(String(thresholdUpAttr ?? '').trim() || '0.8');
  const parsedThresholdDown = parseFloat(String(thresholdDownAttr ?? '').trim() || '0.8');

  const speedThresholdUp =
    Number.isFinite(parsedThresholdUp) && parsedThresholdUp > 0 ? parsedThresholdUp : 0.8;

  const speedThresholdDown =
    Number.isFinite(parsedThresholdDown) && parsedThresholdDown > 0 ? parsedThresholdDown : 0.8;

  const getHideOffset = () => `-${headerSection.offsetHeight}px`;
  const originalTop = headerSection.getBoundingClientRect().top + window.scrollY;
  let isSticky = false;

  function setStickyMode(enabled) {
    if (enabled === isSticky) return;
    isSticky = enabled;

    if (enabled) {
      headerSection.classList.remove('relative');
      headerSection.classList.add('sticky');
    } else {
      headerSection.classList.remove('sticky');
      headerSection.classList.add('relative');
      gsap.set(headerSection, { top: 0 });
      isHidden = false;
    }
  }

  gsap.set(headerSection, { top: 0 });

  function onScroll() {
    const now = performance.now();
    const currentScrollY = window.scrollY;

    // Ignore scroll jumps while a modal or the mobile menu locks the page
    // (e.g. iOS moving the page for the keyboard), so the header stays put.
    const root = document.documentElement;
    if (root.dataset.modalScrollLock !== undefined || root.style.overflow === 'clip') {
      lastScrollY = currentScrollY;
      lastTime = now;
      return;
    }

    const dt = Math.max(now - lastTime, 1);
    const dy = currentScrollY - lastScrollY;
    const speed = dy / dt;

    const passedThreshold = currentScrollY > 80;
    const isPastOriginal = currentScrollY > originalTop + headerSection.offsetHeight;
    const scrollingDownFast = speed > speedThresholdDown;
    const scrollingUpFast = speed < -speedThresholdUp;

    if (currentScrollY <= 1) {
      gsap.killTweensOf(headerSection);
      gsap.set(headerSection, { top: 0 });
      setStickyMode(false);
    }

    if (isPastOriginal && scrollingUpFast) {
      if (!isSticky) {
        setStickyMode(true);
        gsap.set(headerSection, { top: getHideOffset() });
        isHidden = true;
      }
    }

    if (passedThreshold && scrollingDownFast && isSticky && !isHidden) {
      gsap.to(headerSection, {
        top: getHideOffset,
        duration: 0.6,
        ease: 'smooth',
        overwrite: true
      });
      isHidden = true;
    }

    if ((scrollingUpFast || currentScrollY <= 36) && isSticky && isHidden) {
      gsap.to(headerSection, {
        top: 0,
        duration: 0.6,
        ease: 'smooth',
        overwrite: true
      });
      isHidden = false;
    }

    lastScrollY = currentScrollY;
    lastTime = now;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
}

document.addEventListener('DOMContentLoaded', initAutoHideHeader);

class LightboxVideo extends HTMLElement {
  static modal = document.querySelector('[data-lightbox-video="modal"]')
  static bg = document.querySelector('[data-lightbox-video="bg"]')
  static video = document.querySelector('[data-lightbox-video="video"]')

  constructor() {
    super()
    this.handleEscape = this.handleEscape.bind(this)
    this.handleCanPlay = this.handleCanPlay.bind(this)
  }

  connectedCallback() {
    this.addEventListener('click', () => this.open())
  }

  open() {
    const src = this.dataset.src
    if (!src) return

    const video = LightboxVideo.video

    video.src = src
    video.load()

    video.addEventListener('canplay', this.handleCanPlay, { once: true })
  }

  handleCanPlay() {
    const modal = LightboxVideo.modal
    const video = LightboxVideo.video

    modal.classList.remove('hidden')
    modal.classList.add('flex')
    modal.setAttribute('aria-hidden', 'false')

    document.body.classList.add('overflow-hidden')

    video.play()

    document.addEventListener('keydown', this.handleEscape)
  }

  close() {
    const modal = LightboxVideo.modal
    const video = LightboxVideo.video

    modal.classList.add('hidden')
    modal.classList.remove('flex')
    modal.setAttribute('aria-hidden', 'true')

    document.body.classList.remove('overflow-hidden')

    video.pause()
    video.currentTime = 0
    video.removeAttribute('src')
    video.load()

    document.removeEventListener('keydown', this.handleEscape)
  }

  handleEscape(e) {
    if (e.key === 'Escape') this.close()
  }
}

customElements.define('lightbox-video', LightboxVideo)

/* background close */
LightboxVideo.bg.addEventListener('click', () => {
  document.querySelector('lightbox-video')?.close()
})

// class ParalaxImg extends HTMLElement {
//   connectedCallback() {
//     const img = this.querySelector("img")
//     if (!img) return

//     gsap.fromTo(
//       img,
//       { y: "-20%" },
//       {
//         y: "0%",
//         ease: "none",
//         scrollTrigger: {
//           trigger: this,
//           start: "top bottom",
//           ease: "none",
//           end: "bottom top",
//           scrub: true
//         }
//       }
//     )
//   }
// }

// customElements.define("paralax-img", ParalaxImg)